import { Anchor } from './MarketingShell';
import { Band, Card, Faq, DemoCallout } from './sections';

/**
 * Resources: the FAQ in full, and a plain-English glossary.
 *
 * The glossary is deliberately definitional and nothing more. Every entry is a
 * term whose meaning has been stable for years — what a Directed Enhanced
 * Service *is*, not which ones are running this year, what they pay, or who
 * qualifies. Anything with an annual cycle attached to it would be wrong within
 * months and would be quoted back at us in the meantime, and a company selling
 * contract accuracy cannot afford a stale funding claim on its own website.
 *
 * The same reasoning is why there is no "latest guidance" feed here. If it
 * cannot be kept true without someone remembering to update it, it does not
 * belong on a page nobody owns.
 */

const GLOSSARY: { term: string; full?: string; body: string }[] = [
  {
    term: 'PCN',
    full: 'Primary Care Network',
    body:
      'A group of neighbouring GP practices working together under a shared agreement, typically ' +
      'covering a population of around 30,000 to 50,000 patients. Practices remain separate ' +
      'organisations; the network is how they hold and deliver certain services jointly.',
  },
  {
    term: 'ICB',
    full: 'Integrated Care Board',
    body:
      'The statutory NHS body responsible for planning and commissioning health services for a ' +
      'geographical area, and the organisation most primary care contracts are held with.',
  },
  {
    term: 'GMS, PMS and APMS',
    full: 'General, Personal and Alternative Provider Medical Services',
    body:
      'The three contract types under which a practice provides core general practice. GMS is the ' +
      'nationally negotiated contract; PMS is locally agreed on a similar basis; APMS is used ' +
      'where a provider other than a traditional partnership holds the contract.',
  },
  {
    term: 'DES',
    full: 'Directed Enhanced Service',
    body:
      'A service directed nationally and offered to practices or networks on top of core ' +
      'contractual work, with its own specification, requirements and payment arrangements. ' +
      'Participation is normally optional; the requirements, once signed up to, are not.',
  },
  {
    term: 'Enhanced service (LES, LCS)',
    full: 'Local Enhanced Service, Local Commissioned Service',
    body:
      'A service commissioned locally rather than nationally, with a specification written by the ' +
      'commissioner. Naming varies by area, which is one reason the specification itself — rather ' +
      'than what a service is called — is the thing worth reading.',
  },
  {
    term: 'Service specification',
    body:
      'The document that says what a service must do: who is eligible, what has to happen, how it ' +
      'is recorded, what is reported, by when, and what is paid on what condition. It is the ' +
      'document InBrief was built to make usable.',
  },
  {
    term: 'QOF',
    full: 'Quality and Outcomes Framework',
    body:
      'A framework of indicators covering clinical care, public health and practice organisation, ' +
      'against which practices record activity and are remunerated. The indicator set is reviewed ' +
      'periodically, so the current year\'s guidance is always the authority.',
  },
  {
    term: 'ARRS',
    full: 'Additional Roles Reimbursement Scheme',
    body:
      'The arrangement through which primary care networks claim reimbursement for employing ' +
      'certain additional staff roles. The eligible roles and the conditions attached to them are ' +
      'set out in the relevant national guidance.',
  },
  {
    term: 'KPI and indicator',
    full: 'Key performance indicator',
    body:
      'A measurable expectation attached to a service — a number, a proportion, or a deadline — ' +
      'against which delivery is assessed. Indicators are usually spread through a specification ' +
      'rather than gathered in one place, which is why they are easy to miss.',
  },
  {
    term: 'DPIA',
    full: 'Data Protection Impact Assessment',
    body:
      'The assessment an organisation carries out before processing that is likely to present a ' +
      'high risk to individuals. If you are assembling one that involves InBrief, the ' +
      'security and governance page is the place to start.',
  },
  {
    term: 'DSPT',
    full: 'Data Security and Protection Toolkit',
    body:
      'The self-assessment through which organisations handling NHS data demonstrate their data ' +
      'security practices against the national standards.',
  },
  {
    term: 'Federation',
    body:
      'A body formed by a group of practices — often across several networks — to hold contracts, ' +
      'employ staff or deliver services at a larger scale than a single practice or network could.',
  },
];

export function ResourcesPage() {
  return (
    <>
      <Band
        title="Resources"
        heading="h1"
        lede="The questions buyers ask before a procurement conversation happens, a plain-English
              glossary of the terms that appear in primary care contracts, and a product you can
              try without talking to anyone."
      >
        <div className="mk-three">
          <Card title="Try the product">
            <p>The whole interface, with invented data, in its own tab. No sign-up.</p>
            <Anchor to="/demo" className="btn btn-secondary">Interactive demo</Anchor>
          </Card>
          <Card title="Security and governance">
            <p>Data handling, tenant separation, audit, retention and model usage, for a DPIA.</p>
            <Anchor to="/security" className="btn btn-secondary">Read the detail</Anchor>
          </Card>
          <Card title="Pricing and pilots">
            <p>Per-practice pricing, PCN rates, and a twelve-week paid pilot.</p>
            <Anchor to="/pricing" className="btn btn-secondary">See pricing</Anchor>
          </Card>
        </div>
      </Band>

      <Band
        id="faq"
        title="Frequently asked questions"
        tone="tint"
        lede="Answered from what the software does, not from what would sell well."
      >
        <Faq />
      </Band>

      <Band
        id="glossary"
        title="A plain-English glossary"
        lede="Definitions, for someone new to primary care contracting or explaining it to a
              colleague."
      >
        <dl className="mk-glossary">
          {GLOSSARY.map((entry) => (
            <div key={entry.term} className="mk-glossary-entry">
              <dt>
                {entry.term}
                {entry.full ? <span className="mk-glossary-full">{entry.full}</span> : null}
              </dt>
              <dd>{entry.body}</dd>
            </div>
          ))}
        </dl>
        <p className="mk-small">
          These are general explanations of what the terms mean, not guidance on what any scheme
          currently requires or pays. Requirements change, and the specification your organisation
          holds is always the authority.
        </p>
      </Band>

      <Band id="demo" title="See it working" tone="tint">
        <DemoCallout heading="h3" />
      </Band>
    </>
  );
}
